import React from "react";
import { X, Check } from "lucide-react";
import { SignatureModal } from "../SignatureModal";

interface AcroWithoutSectionHtmlProps {
  headersHtml: string;
  isPublishedForm: boolean;
  errorText: string | null;
  showSuccess: boolean;
  mutationIsPending: boolean;
  submitFormPending: boolean;
  publishFormPending: boolean;
  onSaveChanges: () => void;
  onSubmitForm: () => void;
  onPublishForm: () => void;
  // Signature modal props
  sigOpen: boolean;
  signatureValue: string;
  onCloseSignature: () => void;
  onChangeSignature: (val: string) => void;
  onSaveSignature: (signature: string) => void;
  isSaveDisabled?: boolean; // disable save/submit when member is not assigned
}

export const AcroWithoutSectionHtml: React.FC<AcroWithoutSectionHtmlProps> = ({
  headersHtml,
  isPublishedForm,
  errorText,
  showSuccess,
  mutationIsPending,
  submitFormPending,
  publishFormPending,
  onSaveChanges,
  onSubmitForm,
  onPublishForm,
  sigOpen,
  signatureValue,
  onCloseSignature,
  onChangeSignature,
  onSaveSignature,
  isSaveDisabled = false,
}) => {
  return (
    <>
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-end gap-3">
          {errorText && (
            <span className="error-badge" aria-live="assertive">
              <span className="error-badge__icon" aria-hidden="true">
                <X className="w-3 h-3" />
              </span>
              <span className="text-sm text-red-600 truncate max-w-[200px]">
                {errorText}
              </span>
            </span>
          )}
          {showSuccess && (
            <span className="success-badge" aria-live="polite">
              <span className="success-badge__icon" aria-hidden="true">
                <Check className="w-3 h-3" />
              </span>
              <span className="text-sm text-green-600">Saved</span>
            </span>
          )}
          {isPublishedForm && (
            <>
              <button
                type="button"
                onClick={onSaveChanges}
                disabled={isSaveDisabled || mutationIsPending || submitFormPending || publishFormPending}
                className={`px-4 py-1.5 text-sm rounded-full transition ${isSaveDisabled || mutationIsPending || submitFormPending || publishFormPending
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "btn-secondary"
                  }`}
              >
                {mutationIsPending ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                onClick={onSubmitForm}
                disabled={isSaveDisabled || mutationIsPending || submitFormPending || publishFormPending}
                className={`px-4 py-1.5 text-sm rounded-full transition ${isSaveDisabled || mutationIsPending || submitFormPending || publishFormPending
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "btn-primary"
                  }`}
              >
                {submitFormPending ? "Submitting..." : "Submit Form"}
              </button>
            </>
          )}
          {!isPublishedForm && (
            <button
              type="button"
              onClick={onPublishForm}
              disabled={mutationIsPending || submitFormPending || publishFormPending}
              className={`px-4 py-1.5 text-sm rounded-full transition ${mutationIsPending || submitFormPending || publishFormPending
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "btn-primary"
                }`}
            >
              {publishFormPending ? "Publishing..." : "Publish Form"}
            </button>
          )}
        </div>
      </div>

      <div className="flex-auto overflow-y-auto px-6 py-6">
        <div className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-lg p-4">
          <div
            className="prose max-w-none preview-form-content preview-acro-form flex flex-col gap-4"
            dangerouslySetInnerHTML={{ __html: headersHtml }}
          />
        </div>
      </div>

      <SignatureModal
        isOpen={sigOpen}
        value={signatureValue}
        onClose={onCloseSignature}
        onChange={onChangeSignature}
        onSave={onSaveSignature}
      />
    </>
  );
};
